import { AccAnchor } from "@components/base/AccAnchor";
import React, { useRef } from "react";
import { graphql } from "gatsby";
import Layout from "@components/layout";
import Seo from "@components/seo";
import { DateOptions } from "@utils/dateFormat";
import {
  CustomMarkdownComponents,
  MarkdownPageContext,
} from "@utils/MarkdownUtils";
import { AuthorFooterCard } from "@ui/BlogAuthorCards";
import sluggify from "@utils/sluggify";
import { BlogPostJsonLd, GatsbySeo } from "gatsby-plugin-next-seo";
import { useLocation } from "@reach/router";
import BannerSection from "@components/BannerSection";
import RecommendedArticles from "@components/RecommendedArticles";
import "../fonts/ibm-plex-serif-v10-latin-regular.woff2";
import "../styles/blog-post.scss";

import type { DeepNonNullable } from "ts-essentials";
import { GatsbyImage } from "gatsby-plugin-image";
import { LinkIcon } from "@heroicons/react/outline";
import { LocaleSlugMap, updateSlugsToLocale } from "@utils/i18n";
import removeAngleBrackets from "@utils/removeAngleBrackets";
import BlogPostAside from "@components/blogpost-aside";
import PageHeader from "@components/PageHeader";

export const query = graphql`
  query SpecificPostData($slug: String!, $locale: String!, $cutoffDate: Date!) {
    site {
      siteMetadata {
        siteUrl
      }
    }
    strapiGenericText(locale: { eq: $locale }) {
      articleByLabel
      contentsLabel
      lastUpdatedLabel
      shareArticleLabel
      startTrialBannerSubtitle
      startTrialBannerTitle
      startTrialBannerButtonText
      waitText
      recommendedArticles
      errorText
      closeText
    }
    allStrapiBlogPost(
      filter: {
        slug: { eq: $slug }
        locale: { eq: $locale }
        publish_at: { lte: $cutoffDate }
      }
    ) {
      edges {
        node {
          id
          strapi_id
          title
          pageTitle
          slug
          publish_at
          Preview
          locale
          # localizations {
          #   data {
          #     attributes {
          #       locale
          #       slug
          #     }
          #   }
          # }
          Content {
            data {
              Content
            }
            medias {
              url
              alternativeText
              localFile {
                childImageSharp {
                  gatsbyImageData
                }
              }
            }
          }
          FeaturedImage {
            url
            localFile {
              childImageSharp {
                gatsbyImageData(
                  quality: 95
                  placeholder: BLURRED
                  layout: FULL_WIDTH
                )
              }
            }
          }
          blog_author {
            AuthorName
            AuthorJobTitle
            AuthorFacebook
            AuthorTwitter
            AuthorLinkedIn
            AuthorDescription
            AuthorBio {
              data {
                AuthorBio
              }
            }
            AuthorImage {
              localFile {
                childImageSharp {
                  gatsbyImageData(quality: 95)
                }
              }
            }
          }
          seo {
            metaDescription
            metaKeywords
          }
        }
      }
    }
    latestBlogPosts: allStrapiBlogPost(
      limit: 4
      skip: 0
      sort: { publishedAt: DESC }
      filter: {
        locale: { eq: $locale }
        slug: { ne: $slug }
        publish_at: { lte: $cutoffDate }
      }
    ) {
      #  TODO: this does not work as Strapi stripped away number IDs for UUIDs
      #  filter: { strapi_id: { lt: $strapiId } }
      edges {
        node {
          title
          id
          slug
          publish_at
          locale
          FeaturedImage {
            localFile {
              childImageSharp {
                gatsbyImageData
              }
            }
          }
        }
      }
    }
  }
`;

const BlogPost = ({
  data,
  pageContext,
}: {
  data: DeepNonNullable<Queries.SpecificPostDataQuery>;
  pageContext: {
    slug: string;
    locale: string;
    localeSlugMap: LocaleSlugMap;
    blogPath: string;
    hubTranslation: string;
    cutoffDate: string;
  };
}) => {
  // const blogPostAsideRef = useRef<BlogPostAsideRef>(null);

  updateSlugsToLocale(data.allStrapiBlogPost.edges, pageContext.locale);
  const { node } = data?.allStrapiBlogPost?.edges?.[0] || {};
  const {
    articleByLabel,
    contentsLabel,
    shareArticleLabel,
    lastUpdatedLabel,
    recommendedArticles,
  } = data?.strapiGenericText;

  const {
    Content,
    Preview,
    FeaturedImage,
    title,
    pageTitle,
    blog_author,
    publish_at,
    seo,
  } = node || {};
  const { url: featuredImageUrl } = FeaturedImage || {};

  const latestPosts = data?.latestBlogPosts?.edges;
  const h2Regex = /^## (.*?)\n/gim;
  const h2Tags = Content?.data.Content.match(h2Regex);
  // Example result: "## Testing again the blog functionality\n"
  const cleanTags = h2Tags?.map(tag => tag.slice(3, -1));
  const sections: string[] = cleanTags?.reverse() as string[];
  const { siteUrl } = data.site.siteMetadata;
  const { pathname } = useLocation();

  // const setActiveSection = (value: string) => {
  //   if (blogPostAsideRef.current) {
  //     blogPostAsideRef.current.setActiveSection(value);
  //   }
  // };
  /** To Avoid glitching effect on frequent updates of activeSection */
  // const debouncedSection = useDebounce<string>(activeSection, 300);

  /** Custom Rendering for specific Markdown Elements */
  const Rendering = {
    ...CustomMarkdownComponents,
    h2: ({ ...props }) => {
      const { children } = props;
      const text = children[0]?.toString() as string;
      const ref = useRef<HTMLHeadingElement>(null);
      // useOnScreen(ref, text, setActiveSection);
      return (
        <h2
          id={sluggify(text)}
          ref={ref}
          {...props}
          className={`group relative mb-9 ${props.className}`}
        >
          {props.children}
          {/* Replicate: https://www.gatsbyjs.com/plugins/gatsby-remark-autolink-headers/ */}
          {/* as MDX does not work with Strapi v4*/}
          <AccAnchor
            href={`#${props.id}`}
            className="invisible group-hover:visible"
          >
            <LinkIcon className="ml-2 inline-block h-4 w-4" />
          </AccAnchor>
        </h2>
      );
    },
    h3: ({ ...props }) => {
      const { children } = props;
      const text = children[0]?.toString() as string;
      const ref = useRef<HTMLHeadingElement>(null);
      // useOnScreen(ref, text, setActiveSection);
      return (
        <h3
          id={sluggify(text)}
          ref={ref}
          {...props}
          className={`group relative mb-2 ${props.className}`}
        >
          {props.children}
          {/* Replicate: https://www.gatsbyjs.com/plugins/gatsby-remark-autolink-headers/ */}
          {/* as MDX does not work with Strapi v4*/}
          <AccAnchor
            href={`#${props.id}`}
            className="invisible group-hover:visible"
          >
            <LinkIcon className="ml-2 inline-block h-3 w-3" />
          </AccAnchor>
        </h3>
      );
    },
    img: ({ ...props }) => {
      // Check if Gatsby image will exist,
      // if not return the usual image
      props.src = removeAngleBrackets(props.src);
      if (props.src) {
        const gatsbyImage =
          data.allStrapiBlogPost.edges[0].node.Content.medias.find(
            i => i.url === props.src
          );
        // Can be empty for Gifs for example
        if (gatsbyImage && gatsbyImage.localFile.childImageSharp) {
          return (
            <GatsbyImage
              alt={gatsbyImage.alternativeText}
              image={gatsbyImage.localFile.childImageSharp.gatsbyImageData}
            />
          );
        }
      }
      if (
        props.src?.startsWith("/") ||
        props.src?.startsWith("http") ||
        props.src?.startsWith("data:")
      ) {
        return <img {...props} />;
      }
      return null;
    },
  };

  return (
    <Layout
      locale={pageContext.locale}
      localeSlugMap={pageContext.localeSlugMap}
    >
      <Seo
        title={title}
        featuredImageURL={featuredImageUrl}
        metaDescription={seo?.metaDescription || Preview}
        metaKeywords={seo?.metaKeywords || ""}
        {...pageContext}
      />
      <GatsbySeo
        openGraph={{
          type: "article",
          article: {
            publishedTime: publish_at,
          },
          images: [
            {
              url: featuredImageUrl,
            },
          ],
        }}
      />

      <BlogPostJsonLd
        title={title}
        description={Content?.data.Content}
        url={`${siteUrl}${pathname}`}
        authorName={blog_author?.AuthorName}
        datePublished={publish_at}
        images={[featuredImageUrl]}
      />
      <PageHeader
        title={title}
        subtitle={
          <>
            {lastUpdatedLabel + " "}
            {new Date(publish_at).toLocaleString(
              pageContext.locale,
              DateOptions
            )}
          </>
        }
        parents={[
          {
            title: "Blog",
            url:
              (pageContext.locale !== "en" ? `${pageContext.locale}/` : "") +
              pageContext.blogPath,
          },
        ]}
        breadcrumbLabel={pageTitle}
        hubPath={pageContext.hubTranslation}
        hubTitle={pageContext.hubTranslation?.split("/").pop()}
      />
      <div className="full-width-strip bg-accuRankerCream">
        <div className="mx-auto max-w-7xl px-4 pt-4 pb-12">
          <div className="relative mx-auto mt-4 mb-4 grid max-w-7xl grid-cols-6 gap-16 lg:grid-cols-12">
            <div className="blog-post col-span-full lg:col-start-1 lg:col-end-8">
              <MarkdownPageContext overrides={Rendering}>
                {Content?.data.Content}
              </MarkdownPageContext>
            </div>
            <div className="col-span-1 hidden lg:block">
              <BlogPostAside
                blogAuthor={blog_author}
                contentsLabel={contentsLabel}
                shareArticleLabel={shareArticleLabel}
                sections={sections}
              />
            </div>
          </div>
          {blog_author?.AuthorImage && (
            <div className="mt-8 mb-4 grid max-w-3xl grid-cols-5">
              <div className="col-start-1 col-end-6 lg:col-end-5">
                <AuthorFooterCard
                  authorDescription={blog_author.AuthorDescription}
                  authorName={blog_author.AuthorName}
                  authorBio={blog_author.AuthorBio.data.AuthorBio}
                  authorSocial={{
                    facebook: blog_author.AuthorFacebook,
                    linkedin: blog_author.AuthorLinkedIn,
                    twitter: blog_author.AuthorTwitter,
                  }}
                  image={
                    blog_author.AuthorImage.localFile.childImageSharp
                      .gatsbyImageData
                  }
                  articleByLabel={articleByLabel}
                />
              </div>
            </div>
          )}
          {latestPosts?.length && (
            <div className="mx-auto mt-12 mb-4 max-w-7xl">
              <RecommendedArticles
                latestPosts={latestPosts}
                title={recommendedArticles}
                blogSlug={pageContext.blogPath}
              />
            </div>
          )}
          <BannerSection genericText={data.strapiGenericText} />
        </div>
      </div>
    </Layout>
  );
};

export default BlogPost;
